import React, { useEffect, useRef } from "react";
import Hero from "../components/Hero";
import SectionTitle from "../components/SectionTitle";
// import ServiceCard from '../components/ServiceCard';
import "../styles/Home.css";

import { RiGlobalLine } from "react-icons/ri";
import { TbTargetArrow } from "react-icons/tb";
import { PiHospitalFill } from "react-icons/pi";
import { IoSettingsOutline } from "react-icons/io5";
import { RiSecurePaymentLine } from "react-icons/ri";
import { IoSearch } from "react-icons/io5";
import { BsHospital } from "react-icons/bs";
import {
  FaPlaneDeparture,
  FaHandshake,
  FaFileAlt,
  FaBullseye,
  FaGlobe,
} from "react-icons/fa";
import { MdPrecisionManufacturing } from "react-icons/md";

/* ─────────────────────────────────────────────
   Scroll-reveal hook
───────────────────────────────────────────── */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".fade-up");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e, i) => {
          if (e.isIntersecting)
            setTimeout(() => e.target.classList.add("visible"), i * 80);
        });
      },
      { threshold: 0.12 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

/* ─────────────────────────────────────────────
   Counter animation hook
───────────────────────────────────────────── */
function useCounters() {
  const done = useRef(false);
  useEffect(() => {
    const targets = [
      { id: "cnt1", end: 500, suffix: "+" },
      { id: "cnt2", end: 120, suffix: "+" },
      { id: "cnt3", end: 25, suffix: "+" },
      { id: "cnt4", end: 98, suffix: "%" },
    ];
    const items = document.querySelectorAll(".stats-bar__item");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !done.current) {
            e.target.classList.add("visible");
            done.current = true;
            targets.forEach((t) => {
              const el = document.getElementById(t.id);
              if (!el) return;
              let count = 0;
              const step = Math.ceil(t.end / 60);
              const timer = setInterval(() => {
                count = Math.min(count + step, t.end);
                el.textContent = count + t.suffix;
                if (count >= t.end) clearInterval(timer);
              }, 20);
            });
          }
        });
      },
      { threshold: 0.5 },
    );
    items.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);
}

/* ─────────────────────────────────────────────
   Data
───────────────────────────────────────────── */
const INDUSTRIES = [
  {
    img: "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=600&q=80",
    icon: <PiHospitalFill />,
    name: "Healthcare",
    desc: "Placing doctors, nurses, and medical professionals in top global institutions.",
  },
  {
    img: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80",
    icon: <IoSettingsOutline />,
    name: "Engineering & Construction",
    desc: "Civil, mechanical and structural engineers for major international projects.",
  },
  {
    img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80",
    icon: <BsHospital />,
    name: "Hospitality & Tourism",
    desc: "Premium hotel and travel industry placements worldwide.",
  },
  {
    img: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&q=80",
    icon: <MdPrecisionManufacturing />,
    name: "Manufacturing & Logistics",
    desc: "Skilled workers for global supply chain and production facilities.",
  },
];

const WHY_FEATS = [
  {
    icon: <TbTargetArrow />,
    title: "Experienced HR Professionals",
    text: "Our team brings years of international recruitment expertise.",
  },
  {
    icon: <RiGlobalLine />,
    title: "Trusted Global Network",
    text: "Verified employer partnerships across continents.",
  },
  {
    icon: <IoSearch />,
    title: "Transparent Processes",
    text: "No hidden fees. Clear timelines. Honest communication.",
  },
  {
    icon: <RiSecurePaymentLine />,
    title: "End-to-End Support",
    text: "From application to landing — we're with you every step.",
  },
];

const COUNTRIES = [
  {
    img: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=400&q=80",
    flag: "🇷🇺",
    name: "Russia",
    sub: "Eastern Europe & Northern Asia",
  },
  {
    img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400&q=80",
    flag: "🇦🇪",
    name: "Middle East",
    sub: "UAE · Saudi Arabia · Qatar",
  },
  {
    img: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=400&q=80",
    flag: "🇸🇬",
    name: "Singapore",
    sub: "Southeast Asia Hub",
  },
  {
    img: "https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=400&q=80",
    flag: "🇲🇺",
    name: "Mauritius",
    sub: "Indian Ocean Region",
  },
  {
    img: "https://images.unsplash.com/photo-1570789210967-2cac24afeb00?w=400&q=80",
    flag: "🇭🇰",
    name: "Hong Kong",
    sub: "East Asia Financial Hub",
  },
];

/* ── NEW: Services ── */
const SERVICES = [
  {
    icon: <FaPlaneDeparture />,
    title: "Overseas Job Placement",
    desc: "We connect candidates with verified employers across countries in healthcare, IT, construction, hospitality, and more.",
  },
  {
    icon: <FaHandshake />,
    title: "HR Recruitment Solutions",
    desc: "We help international companies find qualified and skilled professionals based on their specific requirements.",
  },
  {
    icon: <FaFileAlt />,
    title: "Visa & Immigration Assistance",
    desc: "Our experts guide you through documentation, visa applications, and compliance processes for a smooth transition.",
  },
  {
    icon: <FaBullseye />,
    title: "Career Counseling",
    desc: "Personalized guidance to help you choose the right country, role, and career path aligned with your goals.",
  },
  {
    icon: <FaGlobe />,
    title: "Pre-Departure Support",
    desc: "From documentation to cultural orientation, we prepare you for a successful international career journey.",
  },
];

/* ── NEW: Testimonials ── */
const TESTIMONIALS = [
  {
    initial: "A",
    name: "Arjun Mehta",
    role: "Software Engineer — Singapore",
    quote:
      '"Pragya HR made my dream of working abroad a reality. Their guidance through the visa process was seamless and professional."',
  },
  {
    initial: "P",
    name: "Priya Sharma",
    role: "Registered Nurse — Germany",
    quote:
      '"From career counseling to pre-departure orientation, every step was handled with genuine care. I couldn\'t have done it without them."',
  },
  {
    initial: "R",
    name: "Rahul Verma",
    role: "Project Manager — UAE",
    quote:
      '"Professional, efficient, and truly invested in your success. They found me the perfect opportunity aligned with my career goals."',
  },
];

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */
const Home = ({ onNavigate }) => {
  useReveal();
  useCounters();

  return (
    <>
      {/* ── Hero Slider ── */}
      <Hero onNavigate={onNavigate} />

      {/* ── Stats Bar ── */}
      <div className="stats-bar">
        {[
          { id: "cnt1", label: "Placements Done" },
          { id: "cnt2", label: "Partner Companies" },
          { id: "cnt3", label: "Countries Served" },
          { id: "cnt4", label: "Client Satisfaction" },
        ].map((s) => (
          <div className="stats-bar__item fade-up" key={s.id}>
            <div className="stats-bar__num" id={s.id}>
              0+
            </div>
            <div className="stats-bar__label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Intro ── */}
      <section className="home-intro">
        <div className="home-intro__text fade-up">
          <SectionTitle
            tag="Who We Are"
            heading="A Trusted Abroad <em>HR Consultancy</em>"
          />
          <p>
            PRAGYA HR SOLUTIONS is dedicated to connecting skilled professionals
            with international employers. With years of experience in
            recruitment, visa assistance, and career guidance, we simplify the
            complex process of working abroad.
          </p>
          <p>
            Whether you're a professional seeking a career abroad or a company
            looking for global talent, we provide expert HR consultancy services
            tailored to your unique goals.
          </p>
          <button
            className="btn-gold"
            style={{ marginTop: 20 }}
            onClick={() => onNavigate("about")}
          >
            Discover Our Story
          </button>
        </div>
        <div className="home-intro__img fade-up">
          <img
            src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=700&q=80"
            alt="Our Team"
            loading="lazy"
          />
          <div className="home-intro__gold-tag">Trusted Since 2024</div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="industries">
        <SectionTitle
          tag="Sectors We Cover"
          heading="Industries We <em>Serve</em>"
        />
        <div className="industry-grid">
          {INDUSTRIES.map((ind) => (
            <div className="industry-card fade-up" key={ind.name}>
              <img src={ind.img} alt={ind.name} loading="lazy" />
              <div className="industry-card__overlay">
                <div className="industry-card__icon">{ind.icon}</div>
                <div className="industry-card__name">{ind.name}</div>
                <div className="industry-card__desc">{ind.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Key Services ── */}
      <section className="home-services">
        <div className="home-services__header fade-up">
          <SectionTitle
            tag="What We Offer"
            heading="Our Key <em>Services</em>"
            style={{ color: "black" }}
          />
        </div>

        <div className="home-services__grid">
          {SERVICES.map((s, i) => (
            <div
              className="home-services__card fade-up"
              key={s.title}
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              <span className="home-services__card-num">0{i + 1}</span>
              <div className="home-services__card-icon">{s.icon}</div>
              <div className="home-services__card-line" />
              <h3 className="home-services__card-title">{s.title}</h3>
              <p className="home-services__card-desc">{s.desc}</p>
              <button
                className="home-services__card-link"
                onClick={() => onNavigate("contact")}
              >
                Enquire Now <span>→</span>
              </button>
            </div>
          ))}

          {/* Dark CTA tile — fills the 6th slot */}
          <div className="home-services__card home-services__card--cta fade-up">
            <p className="home-services__cta-heading">
              Not sure where
              <br />
              to start?
            </p>
            <p className="home-services__cta-body">
              Our counselors will assess your profile and guide you to the right
              service and destination.
            </p>
            <button className="btn-gold" onClick={() => onNavigate("contact")}>
              Book a Consultation
            </button>
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section className="why-section">
        <div className="why-section__img fade-up">
          <img
            src="https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=700&q=80"
            alt="Why Choose Pragya"
            loading="lazy"
          />
        </div>
        <div className="why-section__body fade-up">
          <SectionTitle
            tag="Why Pragya HR"
            heading="The <em>Difference</em><br>We Make"
            sub="We prioritize your success and ensure every step of your journey is handled with care
            and professionalism. Our global network and expert team set us apart."
            style={{ color: "white" }}
          />
          <div className="why-features">
            {WHY_FEATS.map((f) => (
              <div className="why-feat" key={f.title}>
                <div className="why-feat__icon">{f.icon}</div>
                <div className="why-feat__title">{f.title}</div>
                <div className="why-feat__text">{f.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="home-testimonials">
        <div className="home-testimonials__bg-quote">"</div>

        <div className="home-testimonials__header fade-up">
          <SectionTitle
            tag="Success Stories"
            heading="What Our <em>Clients</em> Say"
            center
            style={{ color: "black" }}
          />
        </div>

        <div className="home-testimonials__grid">
          {TESTIMONIALS.map((t, i) => (
            <div
              className="testimonial-card fade-up"
              key={t.name}
              style={{ transitionDelay: `${i * 0.12}s` }}
            >
              <div className="testimonial-card__quote-icon">"</div>
              <div className="testimonial-card__stars">
                {"★★★★★".split("").map((s, j) => (
                  <span key={j}>{s}</span>
                ))}
              </div>
              <p className="testimonial-card__text">{t.quote}</p>
              <div className="testimonial-card__divider" />
              <div className="testimonial-card__author">
                <div className="testimonial-card__avatar">{t.initial}</div>
                <div className="testimonial-card__meta">
                  <span className="testimonial-card__name">{t.name}</span>
                  <span className="testimonial-card__role">{t.role}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Countries ── */}
      <section className="countries-section">
        <SectionTitle
          tag="Global Destinations"
          heading="Countries We <em>Serve</em>"
          center
        />
        <div className="countries-belt">
          {COUNTRIES.map((c) => (
            <div className="country-card" key={c.name}>
              <img src={c.img} alt={c.name} loading="lazy" />
              <div className="country-card__label">
                <div className="country-card__name">{c.name}</div>
                <div className="country-card__sub">{c.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA Band ── */}
      <div className="cta-band">
        <p className="cta-text" style={{ color: "black" }}>
          Ready to start your <em>global career</em>
          <br />
          journey with Pragya?
        </p>
        <button
          className="btn-gold"
          style={{ whiteSpace: "nowrap", padding: "18px 50px", fontSize: 11 }}
          onClick={() => onNavigate("contact")}
        >
          Begin Your Journey
        </button>
      </div>
    </>
  );
};

export default Home;
